import { NameColumn } from '@/components/NameColumn';
import type { Columns } from '@/components/table/types';
import type { Webpage } from '@/types';

/**
 * Builds column definitions for Webpage entity tables
 *
 * Returns columns for:
 * - url: Webpage URL (primary display)
 * - status: Webpage status (Active/Inactive/Pending)
 * - service: Detected service name (if any)
 *
 * The url column uses NameColumn component to display:
 * - URL in bold purple text
 * - Service name below in smaller gray text (e.g., "okta", "ollama")
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildWebpageColumns();
 * // Returns array with url, status, and service columns
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildWebpageColumns(): Columns<Webpage> {
  return [
    {
      id: 'url',
      label: 'URL',
      cell: (item: Webpage) => {
        if (!item?.url) {
          return '-';
        }
        const serviceDescription = item.service || undefined;
        return <NameColumn name={item.url} description={serviceDescription} />;
      },
    },
    {
      id: 'status',
      label: 'Status',
      cell: (item: Webpage) => {
        if (!item?.status) {
          return '-';
        }
        // Map status codes to human-readable labels
        const statusMap: Record<string, string> = {
          A: 'Active',
          I: 'Inactive',
          P: 'Pending',
        };
        return statusMap[item.status] || item.status;
      },
    },
    {
      id: 'service',
      label: 'Service',
      cell: (item: Webpage) => {
        if (!item?.service) {
          return '-';
        }
        return item.service;
      },
    },
  ];
}
